import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {
  STEP_TYPE_TO_USER_FACING_NAME,
  STEP_TYPE_TO_GROUPS,
} from 'graphql-client/__generated/GeneratedEnumMaps';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import MenuOptionWithFloatingSubmenu from 'ui-library/menus/MenuOptionWithFloatingSubmenu';
import {DefaultOptionContent, getOptionRefForIndex} from 'ui-library/menus/MenuUtil';
import Option from 'ui-library/menus/Option';
import OptionContainer from 'ui-library/menus/OptionContainer';

interface StepTypeMenuProps {
  /** Filter which step types are included in the menu. */
  filterStepTypes?: (stepType: PipelineStepType) => boolean;
  onSelect: (stepType: PipelineStepType) => void;
}

/**
 * Function for organizing step types into groups.
 *
 * @param stepTypes list of step types to sort by group
 * @returns a record of groups and their corresponding step types
 */
function sortStepTypesByGroup(stepTypes: Array<PipelineStepType>): Record<string, Array<PipelineStepType>> {
  // TODO: Make groups an enum so we can have universal ordering in all uses.
  const stepTypesByGroup: Record<string, Array<PipelineStepType>> = {
    Transform: [],
    'Combine tables': [],
    Other: [],
  };
  for (const stepType of stepTypes) {
    if (STEP_TYPE_TO_GROUPS[stepType].length === 0) {
      if (stepTypesByGroup.Other === undefined) {
        stepTypesByGroup.Other = [];
      }
      stepTypesByGroup.Other.push(stepType);
    }
    for (const group of STEP_TYPE_TO_GROUPS[stepType]) {
      if (stepTypesByGroup[group] === undefined) {
        stepTypesByGroup[group] = [];
      }
      stepTypesByGroup[group].push(stepType);
    }
  }
  return stepTypesByGroup;
}

/**
 * Menu for selecting pipeline step types.
 */
export default function StepTypeMenu({filterStepTypes, onSelect}: StepTypeMenuProps): JSX.Element {
  const stepTypes = Object.keys(PipelineStepType).filter(
    (stepType: PipelineStepType) => filterStepTypes?.(stepType) ?? true
  );
  const [optionRefs, setOptionRefs] = useState<{[ref: string]: HTMLLIElement | null}>({});
  const stepTypesByGroup = sortStepTypesByGroup(stepTypes as Array<PipelineStepType>);

  function renderOptionContent(optionData: OptionData): JSX.Element {
    return <DefaultOptionContent optionData={optionData} className={css(styles.optionContent)} />;
  }

  return (
    <Menu width={MenuWidth.MEDIUM}>
      {Object.entries(stepTypesByGroup).map(
        ([group, groupedStepTypes], index) =>
          groupedStepTypes.length > 0 && (
            <OptionContainer
              key={group}
              ref={(node) => {
                if (node != null) {
                  const optionRefName = getOptionRefForIndex(index);
                  if (optionRefs[optionRefName] == null) {
                    setOptionRefs((prevRefs) => ({
                      ...prevRefs,
                      [optionRefName]: node.getRef(),
                    }));
                  }
                }
              }}
            >
              <MenuOptionWithFloatingSubmenu
                menuOptionContainer={optionRefs[getOptionRefForIndex(index)]}
                menuOptionIndex={index}
                optionData={{
                  name: group || '',
                  submenu: [],
                }}
                submenuProps={{width: MenuWidth.FIT_CONTENT}}
                renderOptionContentFn={renderOptionContent}
              >
                {groupedStepTypes.map((stepType: PipelineStepType) => (
                  <Option
                    key={stepType}
                    onSelect={() => {
                      onSelect(stepType);
                    }}
                  >
                    {STEP_TYPE_TO_USER_FACING_NAME[stepType]}
                  </Option>
                ))}
              </MenuOptionWithFloatingSubmenu>
            </OptionContainer>
          )
      )}
      {stepTypes.length === 0 && <Option isDisabled={true}>No step types are available</Option>}
    </Menu>
  );
}

const styles = StyleSheet.create({
  optionContent: {
    height: '100%',
  },
});
